import {useEffect, useRef} from 'react';
import {Dayjs} from 'dayjs';
import {ISeasonalDateRange} from './SeasonalDateRangeInput';

// Map day names to numbers (0 = Sunday, 1 = Monday, etc.)
const DAY_NAME_TO_NUMBER: {[key: string]: number} = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Custom hook to automatically calculate annual operating days
 * based on selected days of operation and holidays
 */
export const useCalculateAnnualDays = (
  selectedSeasonYear: string,
  daysOfOperation: string[],
  holidayDates: (Dayjs | null)[],
  setTotalDays: (val: number) => void,
  seasonalDateRanges?: ISeasonalDateRange[],
  seasonalMode?: 'one-day' | 'multiple-days',
) => {
  const previousMode = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any pending calculations
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the calculation to prevent blocking the UI
    timeoutRef.current = setTimeout(() => {
      // Clear totalDays when switching modes
      if (previousMode.current && previousMode.current !== selectedSeasonYear) {
        setTotalDays(0);
      }
      previousMode.current = selectedSeasonYear;

      if (selectedSeasonYear === 'All Year') {
        // If no days selected, set to 0
        if (daysOfOperation.length === 0) {
          setTotalDays(0);
          return;
        }

        // Calculate total occurrences of selected days in a year
        const currentYear = new Date().getFullYear();

        // Count occurrences of each selected day in a year
        let totalOperatingDays = 0;
        const selectedDayNumbers = daysOfOperation.map(
          day => DAY_NAME_TO_NUMBER[day],
        );

        // Count occurrences for each selected day
        selectedDayNumbers.forEach(dayNum => {
          const firstOccurrence = new Date(currentYear, 0, 1);
          while (firstOccurrence.getDay() !== dayNum) {
            firstOccurrence.setDate(firstOccurrence.getDate() + 1);
          }

          let count = 0;
          const tempDate = new Date(firstOccurrence);
          while (tempDate.getFullYear() === currentYear) {
            count++;
            tempDate.setDate(tempDate.getDate() + 7);
          }
          totalOperatingDays += count;
        });

        // Subtract holidays that fall on selected operating days
        let holidaysOnOperatingDays = 0;
        holidayDates.forEach(holidayDate => {
          if (holidayDate) {
            const dayOfWeek = holidayDate.day(); // 0 = Sunday, 1 = Monday, etc.
            if (selectedDayNumbers.includes(dayOfWeek)) {
              holidaysOnOperatingDays++;
            }
          }
        });

        const finalTotal = Math.max(
          0,
          totalOperatingDays - holidaysOnOperatingDays,
        );
        setTotalDays(finalTotal);
      } else if (selectedSeasonYear === 'Seasonally' && seasonalDateRanges) {
        // Filter ranges based on current mode
        const filteredRanges = seasonalMode
          ? seasonalDateRanges.filter(
              range => range.isOneDay === (seasonalMode === 'one-day'),
            )
          : seasonalDateRanges;

        // Calculate for Seasonally mode
        let totalSeasonalDays = 0;

        filteredRanges.forEach(range => {
          if (!range.start_date || !range.end_date) return;

          // Use the isOneDay flag from the range
          if (range.isOneDay === true) {
            // For one-day entries, just count 1 day
            totalSeasonalDays += 1;
          } else {
            // For multiple-days entries, count occurrences of selected days within the range
            if (range.days_of_operation.length > 0) {
              const selectedDayNumbers = range.days_of_operation.map(
                day => DAY_NAME_TO_NUMBER[day],
              );

              // Count each selected day of the week within the date range
              selectedDayNumbers.forEach(dayNum => {
                let tempDate = range.start_date!.clone();

                // Move to the first occurrence of this day within the range
                while (
                  tempDate.day() !== dayNum &&
                  !tempDate.isAfter(range.end_date, 'day')
                ) {
                  tempDate = tempDate.add(1, 'day');
                }

                // Count occurrences of this day within the range
                while (!tempDate.isAfter(range.end_date, 'day')) {
                  totalSeasonalDays += 1;
                  tempDate = tempDate.add(7, 'day');
                }
              });
            }
          }
        });

        setTotalDays(totalSeasonalDays);
      }
    }, 100); // 100ms debounce

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    selectedSeasonYear,
    daysOfOperation,
    holidayDates,
    setTotalDays,
    seasonalDateRanges,
    seasonalMode,
  ]);
};
